import Papa from "papaparse";

const FUTURES_SPECS = {
  ES: { pointValue: 50.0, tickSize: 0.25, tickValue: 12.5 },
  MES: { pointValue: 5.0, tickSize: 0.25, tickValue: 1.25 },
  NQ: { pointValue: 20.0, tickSize: 0.25, tickValue: 5.0 },
  MNQ: { pointValue: 2.0, tickSize: 0.25, tickValue: 0.5 },
};

const getBaseSymbol = (fullSymbol) => {
  const match = fullSymbol.match(/^(ES|MES|NQ|MNQ)/);
  return match ? match[1] : null;
};

const calculateFuturesPnL = (
  entryPrice,
  exitPrice,
  quantity,
  symbol,
  direction
) => {
  const baseSymbol = getBaseSymbol(symbol);

  if (baseSymbol && FUTURES_SPECS[baseSymbol]) {
    const spec = FUTURES_SPECS[baseSymbol];
    const pointDifference =
      direction === "LONG" ? exitPrice - entryPrice : entryPrice - exitPrice;
    return pointDifference * spec.pointValue * quantity;
  }

  return direction === "LONG"
    ? (exitPrice - entryPrice) * quantity
    : (entryPrice - exitPrice) * quantity;
};

export const parseNinjaTraderCSV = (csvContent) => {
  console.log("Starting NinjaTrader CSV parse");

  const results = Papa.parse(csvContent, {
    header: true,
    skipEmptyLines: true,
    dynamicTyping: true,
  });

  const lines = results.data;
  let trades = [];
  let dailyPnL = 0;

  const sortedLines = lines.sort((a, b) => new Date(a.Time) - new Date(b.Time));
  const positionsByInstrument = {};

  // Get today's date at midnight for comparison
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  sortedLines.forEach((line) => {
    if (!line.Instrument || !line.Action || !line.Price || !line.Time) return;

    const instrument = line.Instrument;
    const action = line.Action;
    const quantity = Math.abs(parseInt(line.Quantity));
    const price = parseFloat(line.Price);
    const time = line.Time;

    if (!positionsByInstrument[instrument]) {
      positionsByInstrument[instrument] = {
        entries: [],
        currentPosition: 0,
      };
    }

    const position = positionsByInstrument[instrument];

    if (action.toUpperCase() === "BUY") {
      position.currentPosition += quantity;
      position.entries.push({ action, quantity, price, time });
    } else if (action.toUpperCase() === "SELL") {
      position.currentPosition -= quantity;
      position.entries.push({ action, quantity, price, time });
    }

    if (position.currentPosition === 0 && position.entries.length >= 2) {
      const entry = position.entries[0];
      const exit = position.entries[position.entries.length - 1];
      const direction = entry.action.toUpperCase() === "BUY" ? "LONG" : "SHORT";

      const pnl = calculateFuturesPnL(
        entry.price,
        exit.price,
        entry.quantity,
        instrument,
        direction
      );

      // Check if trade is from today
      const tradeDate = new Date(exit.time);
      tradeDate.setHours(0, 0, 0, 0);
      if (tradeDate.getTime() === today.getTime()) {
        dailyPnL += pnl;
      }

      const baseSymbol = getBaseSymbol(instrument);
      const isFutures = baseSymbol && FUTURES_SPECS.hasOwnProperty(baseSymbol);

      const trade = {
        date: new Date(entry.time).toISOString(),
        symbol: instrument,
        direction: direction,
        entryPrice: entry.price,
        exitPrice: exit.price,
        quantity: entry.quantity,
        pnl: pnl,
        broker: "ninja",
        status: pnl >= 0 ? "WIN" : "LOSS",
        commission: parseFloat(line.Commission || 0),
        notes: "",
        tags: [],
        timestamp: new Date(),
        reward: Math.max(0, pnl),
        risk: Math.abs(Math.min(0, pnl)),
        isFutures: isFutures,
        contractMultiplier: isFutures
          ? FUTURES_SPECS[baseSymbol].pointValue
          : 1,
        brokerType: "futures", // Added broker type identifier
      };

      trades.push(trade);
      position.entries = [];
    }
  });

  return {
    trades,
    accountBalance: "Funded Futures",
    dailyPnL,
    brokerType: "futures",
  };
};

export const validateNinjaFormat = (csvContent) => {
  const results = Papa.parse(csvContent, { header: true });
  const requiredColumns = ["Instrument", "Action", "Quantity", "Price", "Time"];

  const headers = results.meta.fields || [];
  const hasRequiredColumns = requiredColumns.every((col) =>
    headers.includes(col)
  );

  return {
    isValid: hasRequiredColumns,
    message: hasRequiredColumns
      ? "Valid NinjaTrader format"
      : "Invalid file format. Please upload a NinjaTrader execution file.",
  };
};
